import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchGamesPlatformWise, setSelectedGameId, setSelectedGame, fetchMorePlatformGames } from '../redux/slice/game.slice';
import { getGamePath } from '../utils/gamePath';
import { addToCart } from '../redux/slice/user.slice';
import { ChevronLeft, ChevronRight, Loader2, Search, Grid, List } from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import gsap from 'gsap';
import { Helmet } from 'react-helmet-async';
import GameCard, { GameCardSkeleton, PlatformIcon, PlatformColor, PlatformLabel } from '../components/common/GameCard';
import GameListCard, { GameListCardSkeleton } from '../components/common/GameListCard';
import { restoreScrollPosition } from '../utils/scrollRestoration';
import useGridCorners from '../hooks/useGridCorners';

const PLATFORM_DETAILS = [
    {
        id: 'windows',
        label: 'Windows PC',
        icon: FaWindows,
        color: '#0078d7',
        tag: 'PC Master Race',
        desc: 'Ultra high-definition titles, high frame rates, ray-tracing & modding support.'
    },
    {
        id: 'playstation',
        label: 'PlayStation',
        icon: SiPlaystation,
        color: '#00439c',
        tag: 'PS5 & PS4 Digital',
        desc: 'Blockbuster console exclusives, digital keys, and immersive DualSense experiences.'
    },
    {
        id: 'xbox',
        label: 'Xbox',
        icon: FaXbox,
        color: '#107c10',
        tag: 'Series X|S & One',
        desc: 'Next-gen Xbox digital titles, Smart Delivery releases, and ecosystem codes.'
    },
    {
        id: 'nintendo',
        label: 'Nintendo Switch',
        icon: BsNintendoSwitch,
        color: '#e60012',
        tag: 'Handheld & Docked',
        desc: 'Portable adventures, party favorites, and iconic Nintendo eShop releases.'
    },
    {
        id: 'quest',
        label: 'Meta Quest VR',
        icon: SiOculus,
        color: '#00f0ff',
        tag: 'Standalone & PCVR',
        desc: 'Immersive 3D virtual reality worlds, full physical interaction & room-scale VR.'
    },
    {
        id: 'android',
        label: 'Android',
        icon: DiAndroid,
        color: '#3ddc84',
        tag: 'Mobile Gaming',
        desc: 'High-performance mobile gaming titles and APK digital game keys.'
    },
    {
        id: 'ios',
        label: 'iOS',
        icon: FaApple,
        color: '#a3aaae',
        tag: 'Apple Ecosystem',
        desc: 'Sleek iOS arcade games optimized for retina displays on iPhone & iPad.'
    },
];

export const PlatformCardSkeleton = () => (
    <div className="p-4 rounded-2xl bg-[#080814]/40 border border-white/5 flex flex-col items-center justify-center gap-2.5 flex-shrink-0 w-[140px] xs:w-[150px] sm:w-[165px] xl:w-auto xl:flex-shrink animate-pulse snap-center xl:snap-align-none">
        <div className="p-2 rounded-xl bg-white/10 w-10 h-10 flex items-center justify-center">
            <div className="w-5 h-5 rounded-md bg-white/20" />
        </div>
        <div className="h-3.5 w-20 sm:w-24 bg-white/15 rounded-md my-0.5" />
        <div className="h-4.5 w-16 sm:w-20 rounded-full bg-white/10 border border-white/10" />
    </div>
);

export default function Platforms() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const navScrollRef = useRef(null);

    const { platformWiseGames, loading } = useSelector((state) => state.game);
    const [selectedPlatform, setSelectedPlatform] = useState(() => sessionStorage.getItem('cg_active_platform') || 'windows');
    const [hoveredPlatform, setHoveredPlatform] = useState(null);
    const [isInitialFetched, setIsInitialFetched] = useState(() => Boolean(platformWiseGames && Object.keys(platformWiseGames).length > 0));
    const [localGames, setLocalGames] = useState(() => platformWiseGames?.[selectedPlatform]?.games || []);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [currentPage, setCurrentPage] = useState(() => platformWiseGames?.[selectedPlatform]?.currentPage || 1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [sentinelNode, setSentinelNode] = useState(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);



    // Restore scroll position before browser paint or scroll to top on fresh mount
    useLayoutEffect(() => {
        const isReturning = Boolean(sessionStorage.getItem('cg_scroll_/platforms'));
        if (isReturning) {
            restoreScrollPosition('/platforms');
            return;
        }

        window.scrollTo(0, 0);
        if (window.__lenis) {
            window.__lenis.scrollTo(0, { immediate: true, force: true });
        }
    }, []);

    // Callback ref so React notifies when the sentinel DOM node mounts
    const observerTargetRef = useCallback((node) => {
        setSentinelNode(node);
    }, []);

    // Smoothly scroll the selected platform card into full view (centered)
    const scrollToPlatform = useCallback((platformId, smooth = true) => {
        const el = navScrollRef.current, card = el?.querySelector(`[data-platform-id="${platformId}"]`);
        if (!el || !card) return;
        const cRect = el.getBoundingClientRect(), kRect = card.getBoundingClientRect();
        el.scrollTo({
            left: Math.max(0, el.scrollLeft + (kRect.left - cRect.left) - (el.clientWidth - kRect.width) / 2),
            behavior: smooth ? 'smooth' : 'auto'
        });
    }, []);

    const updateScrollButtons = useCallback(() => {
        const el = navScrollRef.current;
        if (el) {
            setCanScrollLeft(el.scrollLeft > 10);
            setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
        }
    }, []);

    const scrollNav = useCallback((dir) => {
        navScrollRef.current?.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const el = navScrollRef.current;
        if (!el) return;
        updateScrollButtons();
        el.addEventListener('scroll', updateScrollButtons, { passive: true });
        window.addEventListener('resize', updateScrollButtons, { passive: true });
        return () => {
            el.removeEventListener('scroll', updateScrollButtons);
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, [updateScrollButtons, isInitialFetched]);

    // Center selected platform card on initial load / platform change
    useEffect(() => {
        if (!isInitialFetched || !selectedPlatform) return;
        const timer = setTimeout(() => {
            scrollToPlatform(selectedPlatform, false);
            updateScrollButtons();
        }, 80);
        return () => clearTimeout(timer);
    }, [isInitialFetched, selectedPlatform, scrollToPlatform, updateScrollButtons]);

    useEffect(() => {
        if (!platformWiseGames || Object.keys(platformWiseGames).length === 0) {
            dispatch(fetchGamesPlatformWise()).finally(() => {
                setIsInitialFetched(true);
            });
        } else {
            setIsInitialFetched(true);
        }
    }, [dispatch, platformWiseGames]);

    useEffect(() => {
        if (platformWiseGames && platformWiseGames[selectedPlatform]) {
            const currentObj = platformWiseGames[selectedPlatform];
            const initialGames = currentObj.games || [];
            const totalCount = currentObj.count || 0;
            setLocalGames(initialGames);
            setCurrentPage(currentObj.currentPage || Math.ceil(initialGames.length / 12) || 1);
            setHasMore(totalCount > initialGames.length);
        }
    }, [platformWiseGames, selectedPlatform]);

    const handlePlatformSelect = useCallback((platformId) => {
        setSelectedPlatform(platformId);
        sessionStorage.setItem('cg_active_platform', platformId);
        sessionStorage.removeItem('cg_scroll_/platforms');
        scrollToPlatform(platformId, true);
        if (platformWiseGames && platformWiseGames[platformId]) {
            const currentObj = platformWiseGames[platformId];
            const initialGames = currentObj.games || [];
            const totalCount = currentObj.count || 0;
            setLocalGames(initialGames);
            setCurrentPage(currentObj.currentPage || Math.ceil(initialGames.length / 12) || 1);
            setHasMore(totalCount > initialGames.length);
        } else {
            setLocalGames([]);
        }
    }, [platformWiseGames, scrollToPlatform]);

    const loadNextPage = useCallback(async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const nextPage = currentPage + 1;
            const result = await dispatch(fetchMorePlatformGames({
                platform: selectedPlatform,
                page: nextPage,
                limit: 12
            })).unwrap();

            if (result.success) {
                const newGames = result.games || [];
                setLocalGames(prev => {
                    const combined = [...prev, ...newGames];
                    const totalCount = platformWiseGames[selectedPlatform]?.count || 0;
                    setHasMore(combined.length < totalCount && newGames.length > 0);
                    return combined;
                });
                setCurrentPage(nextPage);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Failed to load more platform games:", err);
            setHasMore(false);
        } finally {
            setLoadingMore(false);
        }
    }, [dispatch, selectedPlatform, currentPage, hasMore, loadingMore, platformWiseGames]);

    // High performance IntersectionObserver using callback ref sentinelNode
    useEffect(() => {
        if (!sentinelNode) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    loadNextPage();
                }
            },
            { threshold: 0.1, rootMargin: '300px' }
        );

        observer.observe(sentinelNode);
        return () => {
            observer.unobserve(sentinelNode);
        };
    }, [sentinelNode, hasMore, loadingMore, loading, loadNextPage]);

    // Initial reveal animation with GSAP (skip when returning to saved scroll)
    useEffect(() => {
        const isReturning = Boolean(sessionStorage.getItem('cg_scroll_/platforms') || sessionStorage.getItem('cg_last_game_id_/platforms'));
        if (isReturning) return;

        const ctx = gsap.context(() => {
            gsap.fromTo('.platform-reveal',
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.6, stagger: 0.05, ease: 'power3.out' }
            );
        });
        return () => ctx.revert();
    }, []);

    const activeData = useMemo(() => {
        return platformWiseGames[selectedPlatform] || { count: 0, games: [] };
    }, [platformWiseGames, selectedPlatform]);

    const currentPlatformObj = useMemo(() => {
        return PLATFORM_DETAILS.find(p => p.id === selectedPlatform) || PLATFORM_DETAILS[0];
    }, [selectedPlatform]);

    const ActivePlatformIcon = currentPlatformObj.icon;

    // Memoized search filtering for fast rendering
    const displayedGames = useMemo(() => {
        if (!searchQuery.trim()) return localGames;
        const q = searchQuery.toLowerCase();
        return localGames.filter(game =>
            game.title?.toLowerCase().includes(q) ||
            game.category?.name?.toLowerCase().includes(q)
        );
    }, [localGames, searchQuery]);

    const [platformSelectorGame, setPlatformSelectorGame] = useState(null);
    const [selectedPlatformForGame, setSelectedPlatformForGame] = useState('');

    const handleAddToCartClick = useCallback((gameOrEvent, maybeGame) => {
        if (gameOrEvent && typeof gameOrEvent.stopPropagation === 'function') {
            gameOrEvent.stopPropagation();
        }
        const targetGame = maybeGame || gameOrEvent;
        if (!targetGame) return;

        const available = Object.entries(targetGame.platforms || {}).filter(([_, info]) => info?.available);
        if (selectedPlatform && targetGame.platforms?.[selectedPlatform]?.available) {
            dispatch(addToCart({ ...targetGame, selectedPlatform }));
        } else if (available.length === 1) {
            dispatch(addToCart({ ...targetGame, selectedPlatform: available[0][0] }));
        } else if (available.length > 1) {
            setPlatformSelectorGame(targetGame);
            const keys = available.map(a => a[0]);
            setSelectedPlatformForGame(keys.includes(selectedPlatform) ? selectedPlatform : (keys.includes('windows') ? 'windows' : keys[0]));
        } else {
            dispatch(addToCart({ ...targetGame, selectedPlatform: 'windows' }));
        }
    }, [dispatch, selectedPlatform]);

    const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ viewMode });

    const renderedGames = useMemo(() => {
        return displayedGames.map((game, index) => (
            viewMode === 'grid' ? (
                <GameCard
                    key={game.id || game._id}
                    game={game}
                    onAddToCart={handleAddToCartClick}
                    style={getCornerStyle(index, displayedGames.length, gridCols)}
                />
            ) : (
                <GameListCard
                    key={game.id || game._id}
                    game={game}
                    showFilters={false}
                    onAddToCart={handleAddToCartClick}
                    style={getCornerStyle(index, displayedGames.length, gridCols)}
                />
            )
        ));
    }, [displayedGames, viewMode, handleAddToCartClick, gridCols, getCornerStyle]);

    return (
        <div className="min-h-screen bg-transparent text-gray-100 pt-24 pb-12 sm:pt-32 sm:pb-32 relative overflow-hidden font-sans selection:bg-cyber-blue selection:text-black">
            <Helmet>
                <title>Browse Games by Platform | CUBE GAMES</title>
                <meta name="description" content="Explore and filter premium digital games by platform. Find your favorite releases optimized for Windows PC, PlayStation, Xbox, Nintendo Switch, and VR." />
                <meta name="keywords" content="PC games, playstation games, xbox keys, nintendo switch, vr headsets" />
                <link rel="canonical" href="https://cubegames.ca/platforms" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Browse Games by Platform | CUBE GAMES" />
                <meta property="og:description" content="Explore and filter premium digital games by platform. Find your favorite releases optimized for Windows PC, PlayStation, Xbox, Nintendo Switch, and VR." />
                <meta property="og:url" content="https://cubegames.ca/platforms" />
            </Helmet>

            {/* Dynamic Ambient Platform Lighting */}
            <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04] pointer-events-none"></div>

            {/* Dynamic Center Halo Ambient Light matched to platform color */}
            <div
                className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] transition-all duration-700 pointer-events-none opacity-15"
                style={{ backgroundColor: currentPlatformObj.color || '#00f0ff' }}
            ></div>

            <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px] pointer-events-none"></div>
            <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px] pointer-events-none"></div>

            <div className="container mx-auto relative z-10">
                {/* Hero Header */}
                <div className="platform-reveal flex flex-col items-start text-left mb-8 md600:mb-10 space-y-2">
                    <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                        NEXT-GEN GAMING ECOSYSTEMS
                    </p>
                    <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight">
                        Browse By <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Platform</span>
                    </h1>
                    <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
                </div>

                {/* Platforms Navigation Tab Bar */}
                <div className="relative group/nav mb-10">
                    <button
                        type="button"
                        onClick={() => scrollNav('left')}
                        className={`xl:hidden absolute -left-2 sm:-left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-[#080814]/95 border border-cyber-cyan/40 text-cyber-cyan hover:text-white hover:bg-cyber-cyan/20 flex items-center justify-center shadow-[0_0_15px_rgba(0,240,255,0.25)] backdrop-blur-md transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer ${
                            !canScrollLeft ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                        aria-label="Scroll Left"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div
                        ref={navScrollRef}
                        data-lenis-prevent
                        className="flex xl:grid xl:grid-cols-7 gap-3.5 overflow-x-auto xl:overflow-visible no-scrollbar scroll-smooth touch-pan-x py-2.5 px-6 sm:px-7 xl:px-0 scroll-px-6 sm:scroll-px-7 xl:scroll-px-0 snap-x snap-mandatory xl:snap-none -mx-2 xl:mx-0"
                    >
                        {!isInitialFetched || (loading && (!platformWiseGames || Object.keys(platformWiseGames).length === 0)) ? (
                            [...Array(7)].map((_, i) => (
                                <PlatformCardSkeleton key={i} />
                            ))
                        ) : (
                            PLATFORM_DETAILS.map((p) => {
                                const count = platformWiseGames[p.id]?.count || 0;
                                const isSelected = selectedPlatform === p.id;
                                const isHovered = hoveredPlatform === p.id;
                                const isActiveOrHovered = isSelected || isHovered;
                                const Icon = p.icon;

                                return (
                                    <button
                                        key={p.id}
                                        data-platform-id={p.id}
                                        onClick={() => handlePlatformSelect(p.id)}
                                        onMouseEnter={() => setHoveredPlatform(p.id)}
                                        onMouseLeave={() => setHoveredPlatform(null)}
                                        className={`group p-4 rounded-2xl transition-all duration-300 flex flex-col items-center justify-center text-center gap-2.5 relative overflow-hidden backdrop-blur-xl flex-shrink-0 w-[140px] xs:w-[150px] sm:w-[165px] xl:w-auto xl:flex-shrink snap-center xl:snap-align-none
                                            ${isSelected
                                                ? 'scale-[1.03] shadow-2xl'
                                                : isHovered
                                                    ? 'scale-[1.03] bg-[#0c0d20] shadow-xl'
                                                    : 'bg-[#080814]/60 hover:scale-[1.02]'}`}
                                        style={{
                                            borderWidth: '1.5px',
                                            borderStyle: 'solid',
                                            borderColor: isActiveOrHovered ? p.color : `${p.color}40`,
                                            backgroundColor: isSelected
                                                ? `${p.color}20`
                                                : isHovered
                                                    ? `${p.color}15`
                                                    : undefined,
                                            boxShadow: isSelected
                                                ? `0 0 20px ${p.color}40, inset 0 0 12px ${p.color}18`
                                                : isHovered
                                                    ? `0 0 16px ${p.color}30, inset 0 0 10px ${p.color}12`
                                                    : `0 0 8px ${p.color}10`
                                        }}
                                    >
                                        <div
                                            className="absolute top-0 left-0 w-full h-[2px] transition-all duration-300"
                                            style={{
                                                backgroundColor: p.color,
                                                opacity: isActiveOrHovered ? 1 : 0.3,
                                                boxShadow: isActiveOrHovered ? `0 0 12px ${p.color}` : 'none'
                                            }}
                                        />

                                        <div
                                            className="p-2 rounded-xl transition-all duration-300"
                                            style={{
                                                backgroundColor: isActiveOrHovered ? `${p.color}35` : `${p.color}10`,
                                                transform: isActiveOrHovered ? 'scale(1.1)' : 'scale(1)'
                                            }}
                                        >
                                            <Icon
                                                className="w-6 h-6 transition-all duration-300"
                                                style={{ color: p.color }}
                                            />
                                        </div>

                                        <span
                                            className={`text-[11px] font-cyber font-bold uppercase tracking-wider transition-all duration-300 ${isActiveOrHovered ? 'text-white' : 'text-gray-300'}`}
                                            style={{
                                                textShadow: isActiveOrHovered ? `0 0 10px ${p.color}80` : 'none'
                                            }}
                                        >
                                            {p.label}
                                        </span>

                                        <span
                                            className="text-[9px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full border transition-all duration-300 font-semibold"
                                            style={{
                                                borderColor: isActiveOrHovered ? p.color : `${p.color}40`,
                                                backgroundColor: isActiveOrHovered ? `${p.color}40` : `${p.color}15`,
                                                color: isActiveOrHovered ? '#ffffff' : p.color
                                            }}
                                        >
                                            {count} {count === 1 ? 'Game' : 'Games'}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => scrollNav('right')}
                        className={`xl:hidden absolute -right-2 sm:-right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-[#080814]/95 border border-cyber-cyan/40 text-cyber-cyan hover:text-white hover:bg-cyber-cyan/20 flex items-center justify-center shadow-[0_0_15px_rgba(0,240,255,0.25)] backdrop-blur-md transition-all duration-300 hover:scale-110 active:scale-95 cursor-pointer ${
                            !canScrollRight ? 'opacity-0 pointer-events-none' : 'opacity-100'
                        }`}
                        aria-label="Scroll Right"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Platform Section Title Toolbar */}
                <div
                    className="p-4 sm:p-5 rounded-2xl bg-[#080816]/90 backdrop-blur-xl mb-8 relative overflow-hidden group transition-all duration-500"
                    style={{
                        borderColor: `${currentPlatformObj.color}60`,
                        borderWidth: '1.5px',
                        borderStyle: 'solid',
                        boxShadow: `0 0 20px ${currentPlatformObj.color}18, inset 0 0 12px ${currentPlatformObj.color}08`,
                    }}
                >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <div
                                className="p-2.5 rounded-xl transition-all duration-300 flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${currentPlatformObj.color}20`, border: `1px solid ${currentPlatformObj.color}40` }}
                            >
                                <ActivePlatformIcon className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" style={{ color: currentPlatformObj.color }} />
                            </div>
                            <div>
                                <h3 className="font-cyber font-extrabold text-base sm:text-lg text-white uppercase tracking-wider flex items-center gap-2">
                                    <span>Featured {currentPlatformObj.label} Releases</span>
                                </h3>
                                <p className="text-[11px] text-gray-400 font-mono mt-0.5">
                                    {!isInitialFetched || (loading && (!platformWiseGames || Object.keys(platformWiseGames).length === 0)) ? (
                                        <span className="inline-block w-32 h-3 bg-white/10 rounded animate-pulse align-middle" />
                                    ) : (
                                        <>
                                            Showing <span className="font-bold text-cyber-cyan">{displayedGames.length}</span> of <span className="font-bold text-white">{activeData.count}</span> total titles
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col md600:flex-row items-stretch md600:items-center gap-3">
                            <div className="relative flex-1 md600:w-56 md:w-64">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search game here..."
                                    className="w-full bg-[#151a26] border border-white/[0.08] focus:border-white/20 focus:bg-[#1a2130] text-gray-100 text-xs rounded-xl py-2 sm:py-2.5 pl-10 pr-8 outline-none transition-all placeholder:text-gray-500 font-sans"
                                    style={{ outline: 'none', boxShadow: 'none' }}
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs font-bold"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            <div className="w-full md600:w-auto flex items-center gap-2.5 justify-between md600:justify-start">
                                <div className="hidden md600:flex items-center gap-0.5 sm:gap-1 p-0.5 sm:p-1 bg-[#151a26] rounded-xl border border-white/[0.08] flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('grid')}
                                        className={`p-1.5 sm:p-2 rounded-lg transition-all duration-300 ${viewMode === 'grid'
                                            ? 'bg-white text-black shadow-md shadow-white/10'
                                            : 'text-gray-400 hover:text-white'
                                            }`}
                                        title="Grid View"
                                        aria-label="Grid View"
                                    >
                                        <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('list')}
                                        className={`p-1.5 sm:p-2 rounded-lg transition-all duration-300 ${viewMode === 'list'
                                            ? 'bg-white text-black shadow-md shadow-white/10'
                                            : 'text-gray-400 hover:text-white'
                                            }`}
                                        title="List View"
                                        aria-label="List View"
                                    >
                                        <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                </div>

                                {/* <button
                                    onClick={() => navigate(`/all-games?platform=${selectedPlatform}`)}
                                    className="w-full md600:w-auto px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-[#151a26] border border-white/[0.08] text-xs font-cyber font-bold text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130] transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wider group/btn shadow-sm flex-shrink-0"
                                >
                                    <span>Browse Catalog</span>
                                    <ChevronRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
                                </button> */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Loading / Empty State / Games Grid */}
                {!isInitialFetched || (loading && displayedGames.length === 0) ? (
                    <div
                        ref={setGridRef}
                        className={viewMode === 'grid'
                            ? 'grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full'
                            : 'grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 w-full'}>
                        {[...Array(12)].map((_, i) => (
                            viewMode === 'grid' ? (
                                <GameCardSkeleton
                                    key={i}
                                    style={getCornerStyle(i, 12, gridCols)}
                                />
                            ) : (
                                <GameListCardSkeleton
                                    key={i}
                                    style={getCornerStyle(i, 12, gridCols)}
                                />
                            )
                        ))}
                    </div>
                ) : displayedGames.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center">
                        <Search className="w-12 h-12 text-gray-700 mb-4 animate-pulse" />
                        <h3 className="text-xl font-cyber font-semibold text-white uppercase mb-2">No games found</h3>
                        <p className="text-gray-500 font-cyber text-xs uppercase tracking-widest">Try adjusting your search or switching platforms.</p>
                    </div>
                ) : (
                    <>
                        <div
                            ref={setGridRef}
                            className={viewMode === 'grid'
                                ? 'grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full'
                                : 'grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 w-full'}>
                            {renderedGames}
                        </div>

                        {/* Sentinel target for IntersectionObserver Infinite Scroll */}
                        <div ref={observerTargetRef} className="h-6 w-full" />

                        {/* Subtle Scroll loading indicator */}
                        {(loading || loadingMore) && displayedGames.length > 0 && (
                            <div className="flex justify-center mt-6 mb-8">
                                <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.02] border border-white/5 text-cyber-cyan font-cyber animate-pulse">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>LOADING MORE GAMES...</span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Platform Selector Modal */}
            {platformSelectorGame && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
                    <div className="w-full max-w-md bg-[#0c0c18] border border-cyber-cyan/30 rounded-[1.5rem] sm:rounded-[2.5rem] p-5 sm:p-8 shadow-[0_0_30px_rgba(0,240,255,0.15)] relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink"></div>

                        <div className="space-y-6">
                            <div className="text-center space-y-2">
                                <h3 className="text-xl font-cyber font-black text-white uppercase tracking-tight">Select Platform</h3>
                                <p className="text-gray-400 text-xs font-medium">Choose your target platform for <span className="text-cyber-cyan">{platformSelectorGame.title}</span></p>
                            </div>

                            <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                                {Object.entries(platformSelectorGame.platforms || {})
                                    .filter(([_, info]) => info?.available)
                                    .map(([key, info]) => {
                                        const isSelected = selectedPlatformForGame === key;
                                        const Icon = PlatformIcon[key] || FaWindows;
                                        const label = PlatformLabel[key] || key;
                                        const brandColor = PlatformColor[key] || '#9ca3af';
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => setSelectedPlatformForGame(key)}
                                                className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all text-left
                                                    ${isSelected
                                                        ? 'bg-cyber-cyan/10 border-cyber-cyan text-white shadow-[0_0_15px_rgba(0,240,255,0.1)]'
                                                        : 'bg-white/[0.02] border-white/5 text-gray-400 hover:text-white hover:bg-white/[0.04]'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon className="w-4 h-4" style={{ color: brandColor }} />
                                                    <span className="text-xs font-cyber font-bold uppercase tracking-wide">{label}</span>
                                                </div>
                                                <span className="text-xs font-mono font-bold text-white">
                                                    {info.price === 0 ? 'FREE' : `$${info.price}`}
                                                </span>
                                            </button>
                                        );
                                    })}
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => { setPlatformSelectorGame(null); setSelectedPlatformForGame(''); }}
                                    className="flex-1 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        dispatch(addToCart({ ...platformSelectorGame, selectedPlatform: selectedPlatformForGame }));
                                        setPlatformSelectorGame(null);
                                        setSelectedPlatformForGame('');
                                    }}
                                    disabled={!selectedPlatformForGame}
                                    className="flex-1 py-3.5 rounded-2xl bg-cyber-cyan/20 border border-cyber-cyan/40 text-cyber-cyan font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-cyan/30 disabled:opacity-40 transition-all"
                                >
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
